import React, { useState, useMemo } from 'react';
import { Repeat, Plus, Trash2, Search, AlertTriangle, ArrowRightLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import Button from '../common/Button';

const fmtRs = (n) => `Rs. ${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const stockOf = (p) => p.currentStock ?? p.stock ?? p.quantity ?? 0;

export default function ExchangeModal({
  sale, items = [], refunds = [], products = [], productLoading = false,
  onClose, onExchange,
}) {
  const [returnQtys, setReturnQtys] = useState({});
  const [newItems, setNewItems] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [reason, setReason] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [loading, setLoading] = useState(false);

  const refundedQtyMap = useMemo(() => {
    const map = {};
    refunds.forEach((r) => {
      (r.items || []).forEach((it) => {
        map[it.itemId] = (map[it.itemId] || 0) + it.quantityRefunded;
      });
    });
    return map;
  }, [refunds]);

  const maxQtyMap = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      map[it.itemId] = Math.max(0, it.quantity - (refundedQtyMap[it.itemId] || 0));
    });
    return map;
  }, [items, refundedQtyMap]);

  const returnedValue = useMemo(
    () => items.reduce((sum, it) => sum + (returnQtys[it.itemId] || 0) * (it.unitPrice || 0), 0),
    [items, returnQtys]
  );

  const newTotal = useMemo(
    () => newItems.reduce((sum, it) => sum + it.quantity * it.unitPrice, 0),
    [newItems]
  );

  const difference = useMemo(() => newTotal - returnedValue, [newTotal, returnedValue]);
  const amountDue = Math.max(0, difference);
  const cashBack = difference < 0 ? Math.abs(difference) : 0;

  const searchResults = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    if (!q) return [];
    return products
      .filter((p) => (p.productName || p.name || '').toLowerCase().includes(q))
      .filter((p) => !newItems.some((n) => (n.productId || n.id) === (p.productId || p._id || p.id)))
      .slice(0, 8);
  }, [products, itemSearch, newItems]);

  const addReturn = (itemId, val) => {
    const max = maxQtyMap[itemId] || 0;
    setReturnQtys((p) => ({ ...p, [itemId]: Math.max(0, Math.min(max, Number(val) || 0)) }));
  };

  const addNewItem = (product) => {
    setNewItems((p) => [
      ...p,
      {
        productId: product.productId || product._id || product.id,
        productName: product.productName || product.name,
        quantity: 1,
        unitPrice: Number(product.salePrice ?? product.price ?? 0),
      },
    ]);
    setItemSearch('');
  };

  const editNewItem = (idx, patch) => {
    setNewItems((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const handleSubmit = async () => {
    const returned = items
      .filter((it) => (returnQtys[it.itemId] || 0) > 0)
      .map((it) => ({ itemId: it.itemId, quantity: returnQtys[it.itemId] }));
    if (returned.length === 0) {
      toast.error('Select at least one item to return');
      return;
    }
    if (newItems.length === 0 || newTotal <= 0) {
      toast.error('Add at least one replacement item');
      return;
    }
    const payload = {
      items: returned,
      newItems: newItems.map((it) => ({
        productId: it.productId,
        quantity: it.quantity,
        unitPrice: it.unitPrice,
        saleUnit: 'PIECE',
        piecesDeducted: it.quantity,
      })),
      reason: reason.trim() || undefined,
      paymentMethod,
    };
    setLoading(true);
    try {
      await onExchange(payload);
      toast.success(cashBack > 0 ? `Exchange done. Cash back to customer: ${fmtRs(cashBack)}` : 'Exchange completed successfully');
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Exchange failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Change / Exchange Items"
      description={`Order ${sale?.transactionId || ''}`}
      size="lg"
    >
      <div className="space-y-5">
        <section>
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Returned Items
          </h4>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/40 text-xs text-gray-500 dark:text-gray-400 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Product</th>
                  <th className="px-3 py-2 font-medium text-center">Return</th>
                  <th className="px-3 py-2 font-medium text-right">Credit</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const max = maxQtyMap[it.itemId] || 0;
                  const q = returnQtys[it.itemId] || 0;
                  return (
                    <tr key={it.itemId} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="px-3 py-2">
                        <p className="font-medium text-gray-900 dark:text-white">{it.productName}</p>
                        <p className="text-xs text-gray-400">{fmtRs(it.unitPrice)} each • sold {it.quantity}</p>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="number"
                          min={0}
                          max={max}
                          value={q || ''}
                          disabled={max <= 0}
                          onChange={(e) => addReturn(it.itemId, e.target.value)}
                          placeholder={max <= 0 ? '—' : '0'}
                          className="w-16 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                        />
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-white">
                        {q > 0 ? fmtRs(q * (it.unitPrice || 0)) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Replacement Items
          </h4>
          <div className="relative mb-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder="Search products to add..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {itemSearch.trim() && (
              <div className="absolute top-full left-0 right-0 mt-1 z-10 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {productLoading ? (
                  <p className="px-3 py-2 text-xs text-gray-400">Loading products...</p>
                ) : searchResults.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-gray-400">No products found</p>
                ) : (
                  searchResults.map((p) => (
                    <button
                      key={p.productId || p._id || p.id}
                      type="button"
                      onClick={() => addNewItem(p)}
                      disabled={stockOf(p) <= 0}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 ${stockOf(p) <= 0 ? 'cursor-not-allowed' : ''}`}
                    >
                      <span className="text-sm text-gray-900 dark:text-white">{p.productName || p.name}</span>
                      <span className="text-xs text-gray-400">{fmtRs(p.salePrice ?? p.price ?? 0)}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {newItems.length === 0 ? (
            <p className="text-sm text-gray-400">No replacement items added yet.</p>
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {newItems.map((it, idx) => (
                    <tr key={idx} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                      <td className="px-3 py-2">
                        <p className="font-medium text-gray-900 dark:text-white">{it.productName}</p>
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          min={1}
                          value={it.quantity || ''}
                          onChange={(e) => editNewItem(idx, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                          className="w-16 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          min={0}
                          step="0.5"
                          value={it.unitPrice || ''}
                          onChange={(e) => editNewItem(idx, { unitPrice: Number(e.target.value) || 0 })}
                          className="w-24 px-2 py-1 text-sm text-right border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-2 py-2 text-right font-medium text-gray-900 dark:text-white">
                        {fmtRs(it.quantity * it.unitPrice)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setNewItems((p) => p.filter((_, i) => i !== idx))}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-4 space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">Returned value</span>
            <span className="font-medium text-gray-900 dark:text-white">{fmtRs(returnedValue)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">New items total</span>
            <span className="font-medium text-gray-900 dark:text-white">{fmtRs(newTotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">Difference</span>
            <span className={`font-semibold ${difference < 0 ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>
              {difference < 0 ? `${fmtRs(amountDue)} credit, ${fmtRs(cashBack)} cash back` : fmtRs(amountDue)}
            </span>
          </div>
        </section>

        {amountDue > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-300">Customer pays</span>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="CASH">CASH</option>
              <option value="ONLINE">ONLINE</option>
            </select>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">{fmtRs(amountDue)}</span>
          </div>
        )}

        {cashBack > 0 && (
          <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
            The returned items are worth more than the replacements. Give {fmtRs(cashBack)} cash back to the customer.
          </p>
        )}

        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for exchange (optional)"
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
        />

        <div className="flex items-center justify-between pt-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            <ArrowRightLeft size={15} className="inline mr-1.5 text-blue-500" />
            {amountDue > 0
              ? `Collect ${fmtRs(amountDue)}`
              : cashBack > 0
                ? `Pay ${fmtRs(cashBack)} back`
                : 'Exact value exchange'}
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button variant="primary" icon={Repeat} loading={loading} onClick={handleSubmit}>
              Complete Exchange
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}